import React, { useEffect, useState } from 'react'
import { Alert, Box, Button, ButtonBase, Chip, Grid, Link, Paper, Snackbar, Stack, Switch, TextField, Typography } from '@mui/material'
import {
  ChevronRightRounded, DesktopWindowsOutlined, DnsOutlined, ExpandLessRounded, ExpandMoreRounded, LockOutlined, VideocamRounded
} from '@mui/icons-material'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useUser } from '../../generic/hooks/useUser'
import useSensitiveScreens from '../useSensitiveScreens'
import { clearUnsavedChanges, markUnsavedChanges } from '../../generic/utilities/unsavedChangesGuard'
import SensitiveMarkErrorSnackbar from '../SensitiveMarkErrorSnackbar'
import { FROM_CAMERA_CONTROL } from '../visionIncidentFormat'
import { useVision } from '../visionContext'
import { BackLink, FailureNotice, ResultNotice } from './CoveredNotices'
import useCameraControl from './useCameraControl'
import useSensitiveItems from './useSensitiveItems'
import SingleItems from './SingleItems'
import useVisionSessions from './useVisionSessions'
import SessionsBlock from './SessionsBlock'
import PhoneImagesBlock from './PhoneImagesBlock'
import useEvidenceCount from './useEvidenceCount'
import { deleteAllEvidence } from '../visionApi'
import { refusalText } from '../PhoneEvidence'
import StepCard from './StepCard'
import ActivationDialog from './ActivationDialog'
import {
  ADVANCED_FIELDS, COVER_MODES, canConfigureCameraControl, DEFAULT_MAX_WATCHED_SESSIONS, DEFAULT_PHONE_EVIDENCE_DAYS, FAILURE_COVER_REASONS, MODELS, RULE_GROUPS, RULES_HELP, V3_RANGES, VISION_COVER_MODE,
  VISION_ENABLED, VISION_MAX_WATCHED_SESSIONS, VISION_MODEL, VISION_PHONE_EVIDENCE, VISION_PHONE_EVIDENCE_BLUR_FACES,
  VISION_PHONE_EVIDENCE_DAYS, VISION_SCOPE, VISION_UNWATCHED_SHOWS_PROTECTED, SCOPES, formatNumber, formatPercent, invalidSettings, nothingProtected,
  parseNumber, protectedText, scopeOf, serviceState, settingsMessageText
} from './cameraControlRules'

const MODEL_HELP = [
  'Reacts sooner. Suitable for a server without GPU.',
  'Fewer false alarms, but takes twice as long to cover.',
  'Loaded only when someone opens protected content and unloaded after 10 min without use.',
  'Photo cameras are not detected yet.'
]

// Design (Main.dc.html, PO 2026-10-22): the icon of each scope card, and how much what does not apply is dimmed.
const SCOPE_ICON = {
  SENSITIVE: <LockOutlined data-testid='LockOutlinedIcon' sx={{ color: '#FF8F00' }} />,
  ALL_MIC: <DesktopWindowsOutlined data-testid='DesktopWindowsOutlinedIcon' sx={{ color: 'primary.main' }} />
}
const DIMMED_OPACITY = 0.45

// Design (Main.dc.html): a selectable card, green when chosen.
const cardSx = (selected) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 1.5,
  p: 2,
  width: '100%',
  height: '100%',
  borderRadius: 3,
  textAlign: 'left',
  bgcolor: selected ? '#2B3A31' : '#383838',
  border: '2px solid',
  borderColor: selected ? 'primary.main' : '#555555'
})

// The screen itself, mounted again after a good save so it asks Sun for its data again (H-9).
const CameraControlScreen = ({ onSaved, onError, onNotice, backofficeLink, cover }) => {
  const { t_, i18n } = useSmartTranslation()
  const language = i18n.language
  const navigate = useNavigate()
  const { stored, values, setValue, summary, saveSettings, saveEnabled, saveAtOnce, changedKeys, discard } = useCameraControl(true, language)
  const sensitiveScreens = useSensitiveScreens(true)
  // C15.3 (H-C15-8): the single items, also with the vision off, like the screens.
  const sensitiveItems = useSensitiveItems()
  const [advanced, setAdvanced] = useState(false)
  const [confirming, setConfirming] = useState(false)
  // The value the switch is being saved to, shown while Sun answers ('' when none).
  const [savingEnabled, setSavingEnabled] = useState('')
  // Reopening 5 (RN151, S384, S385): the value "Save phone image" is being saved to, shown while Sun answers ('' when none).
  const [savingImage, setSavingImage] = useState('')

  // S277 (H6, RN99.1): vision.force-disabled on the server overrides VISION_ENABLED: the page shows it off and the switch
  // cannot be used.
  const forced = summary.forceDisabled === true
  const enabled = !forced && (savingEnabled || values[VISION_ENABLED]) === 'true'
  // C14.3 (S285): this session is covered by a failure (RN114) and nothing has been decided yet.
  const failing = cover.failure && cover.decision === '' && enabled
  // RN101 (S270, S271): pending changes are what the fixed bar saves; MIC's unsaved changes guard asks before leaving.
  const dirty = changedKeys.length > 0 || sensitiveScreens.hasChanges || sensitiveItems.hasChanges

  useEffect(() => {
    if (dirty) {
      markUnsavedChanges()
    } else {
      clearUnsavedChanges()
    }
  }, [dirty])

  useEffect(() => () => clearUnsavedChanges(), [])
  const coverMode = String(values[VISION_COVER_MODE] || '').toUpperCase() === 'SENSITIVE_ONLY' ? 'SENSITIVE_ONLY' : 'ALL'
  const model = String(values[VISION_MODEL] || '').toUpperCase() === 'PRECISE' ? 'PRECISE' : 'FAST'
  const scope = scopeOf(values[VISION_SCOPE])
  // C13.7: with All of MIC everything covers whole, so the screens and the cover mode do not apply (shown disabled).
  const scopeIsAllMic = scope === 'ALL_MIC'
  const activeVigilances = typeof summary.activeVigilances === 'number' ? summary.activeVigilances : 0

  const invalid = invalidSettings(values)
  // Reopening 4 (C16.7): the sessions of MIC, only with the vision on; the counter is over the SAVED maximum (S341).
  const visionSessions = useVisionSessions(enabled, onError)
  const savedMax = parseNumber(stored[VISION_MAX_WATCHED_SESSIONS])
  const storedMax = Number.isInteger(savedMax) && savedMax >= 1 ? savedMax : DEFAULT_MAX_WATCHED_SESSIONS
  const watchedCount = visionSessions.sessions.filter((session) => session.status === 'WATCHED').length
  // C17.10: how many phone images can be deleted; deleting them all is an action, not a pending change.
  // The count is on the admins' list also while covered (S404, H-C17b-2); deleting is not (DI-9: a 423 while covered in ALL).
  const evidence = useEvidenceCount()
  const deleteAllImages = async () => {
    try {
      const answer = await deleteAllEvidence()
      onNotice({ key: '{{count}} images deleted', options: { count: answer && typeof answer.deleted === 'number' ? answer.deleted : 0 } })
    } catch (error) {
      // S414: by the code of the answer, as the log does, never by Sun's literal (a 500 has no key of its own).
      onError({ text: refusalText(error && error.status, t_) })
    }
    evidence.refresh()
  }
  // Reopening 5 (C17.7): the image switch shows what is saved; the dialogs say the SAVED blur and days, never pending ones.
  const imageSaved = String(savingImage || stored[VISION_PHONE_EVIDENCE] || '').toUpperCase() === 'YES'
  const storedImageSaved = String(stored[VISION_PHONE_EVIDENCE] || '').toUpperCase() === 'YES'
  const savedBlur = String(stored[VISION_PHONE_EVIDENCE_BLUR_FACES] || '').toUpperCase() !== 'NO'
  const storedDays = parseNumber(stored[VISION_PHONE_EVIDENCE_DAYS])
  const savedDays = Number.isInteger(storedDays) && storedDays >= 1 ? storedDays : DEFAULT_PHONE_EVIDENCE_DAYS
  const service = serviceState(summary)
  const modelText = MODELS.find((option) => option.value === model).text
  const coverModeText = COVER_MODES.find((mode) => mode.value === coverMode).text

  // RN54: first the settings, then the marks, only if the settings were saved: one batch with the screens that changed and
  // the single items protected or removed (H-C15-1). H-14: with an invalid value nothing is sent. A rejected settings save
  // shows Sun's literal translated (S117, C3); a rejected marking keeps the changes pending and shows its own snackbar
  // (S139, S297).
  const save = async () => {
    if (Object.keys(invalid).length > 0) {
      return
    }
    try {
      await saveSettings()
    } catch (error) {
      onError((error && error.message) || '¡Ups, something has gone wrong!')
      return
    }
    if (!(await sensitiveScreens.save(sensitiveItems.changes))) {
      return
    }
    // H-5: a new model with vigilances in force takes a few seconds to load.
    const modelChanged = String(stored[VISION_MODEL] || '').toUpperCase() !== model
    onSaved(modelChanged && activeVigilances > 0)
  }

  // RN101 (S268, S269): the switch is an action, saved at once and alone (R-4); on a failure it goes back to the stored
  // value and the error shows translated (R-3). The other pending changes are not touched.
  const saveSwitch = async (value) => {
    setSavingEnabled(value)
    try {
      await saveEnabled(value)
      // C14.3 (S287): turned off after arriving covered, the page says so (R-8).
      if (value === 'false' && cover.arrivedCovered) {
        cover.decide('off')
      }
    } catch (error) {
      onError((error && error.message) || '¡Ups, something has gone wrong!')
    } finally {
      setSavingEnabled('')
    }
  }

  // RN151 (S384, S385): "Save phone image" is saved at once and alone, after its dialog when turned on; on a failure it goes
  // back to the stored value and the error shows translated. The pending changes of the bar are not touched.
  const saveImage = async (on) => {
    const value = on ? 'YES' : 'NO'
    setSavingImage(value)
    try {
      await saveAtOnce(VISION_PHONE_EVIDENCE, value)
    } catch (error) {
      onError((error && error.message) || '¡Ups, something has gone wrong!')
    } finally {
      setSavingImage('')
    }
  }

  // RN55: turning a stored "off" on asks to confirm first; turning it off asks nothing.
  const toggleEnabled = (checked) => {
    if (forced) {
      return
    }
    if (checked && stored[VISION_ENABLED] !== 'true') {
      setConfirming(true)
      return
    }
    saveSwitch(checked ? 'true' : 'false')
  }

  const activate = () => {
    setConfirming(false)
    saveSwitch('true')
  }

  const discardAll = () => {
    discard()
    sensitiveScreens.discard()
    sensitiveItems.discard()
  }

  const numberField = (definition) => (
    <TextField
      key={definition.key}
      label={t_(definition.label)}
      value={values[definition.key] || ''}
      onChange={(event) => setValue(definition.key, event.target.value)}
      size='small'
      error={invalid[definition.key] === true}
      inputProps={{
        inputMode: 'decimal',
        min: V3_RANGES[definition.key].min,
        max: V3_RANGES[definition.key].max,
        step: V3_RANGES[definition.key].step
      }}
      InputProps={{ endAdornment: definition.unit ? <Typography variant='body2' sx={{ color: 'text.secondary', whiteSpace: 'nowrap', ml: 1 }}>{t_(definition.unit)}</Typography> : undefined }}
      sx={{ minWidth: 200 }}
    />
  )

  return (
    <Stack data-testid='camera-control' spacing={3} sx={{ pb: dirty ? 10 : 0 }}>
      <Stack spacing={0.5}>
        {/* C14.3 (S285): arriving covered, the way back above the breadcrumb. */}
        {cover.arrivedCovered && <BackLink onBack={() => navigate(-1)} t_={t_} arrow />}
        {/* Main.dc.html (Judge J-9): "Backoffice" leads to BACKOFFICE, as its top bar entry; the global unsaved changes
            guard asks first when something is pending (S271). */}
        <Typography data-testid='camera-control-breadcrumb' variant='body2' sx={{ color: 'text.secondary' }}>
          {/* C14.3 (DR-3): for ADMIN the rest of BACKOFFICE is not theirs, so the word is not a link. */}
          {backofficeLink
            ? <Link component={RouterLink} to='/backoffice/proxiedServices' underline='hover' color='inherit'>{t_('Backoffice')}</Link>
            : t_('Backoffice')} › {t_('Camera control')}
        </Typography>
        <Typography variant='h4' component='h1' sx={{ fontWeight: 600 }}>{t_('Camera control')}</Typography>
      </Stack>

      <Paper
        component='section'
        aria-label={t_('Status')}
        sx={{
          p: 3,
          borderRadius: 4,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2.5,
          bgcolor: enabled ? '#2B3A31' : '#3A3A3A',
          border: '2px solid',
          borderColor: enabled ? 'primary.main' : '#5A5A5A'
        }}
      >
        <Stack direction='row' alignItems='center' spacing={2.25}>
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: enabled ? 'primary.main' : '#555555', color: enabled ? '#133222' : '#E6E6E6' }}>
            <VideocamRounded />
          </Box>
          <Stack spacing={0.5}>
            <Typography variant='h5' sx={{ fontWeight: 700, color: enabled ? 'primary.main' : '#E6E6E6' }}>{enabled ? t_('Activated') : t_('Deactivated')}</Typography>
            <Typography variant='body2' sx={{ color: '#CFCFCF' }}>
              {failing && typeof summary.coveredSessions === 'number'
                ? t_('Right now: {{count}} users covered by the fault', { count: summary.coveredSessions })
                : t_('Right now: {{count}} users with the camera in use', { count: activeVigilances })}
            </Typography>
            {/* S277: say why nothing is watched, with the server icon of the design. */}
            {forced && (
              <Typography variant='body2' sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'warning.light', fontWeight: 600 }}>
                <DnsOutlined fontSize='small' />
                {t_('Camera control has been turned off on the server')}
              </Typography>
            )}
          </Stack>
        </Stack>
        {/* Design (S268): the switch says the action it does. */}
        <Stack direction='row' alignItems='center' spacing={1} sx={{ minHeight: 52, pl: 1, pr: 2.25, borderRadius: 3, border: '1px solid #5A5A5A', bgcolor: '#2F2F2F' }}>
          <Switch
            checked={enabled}
            disabled={forced}
            onChange={(event) => toggleEnabled(event.target.checked)}
            inputProps={{ role: 'switch', 'aria-label': t_('Camera control') }}
          />
          <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>{enabled ? t_('Deactivate') : t_('Activate')}</Typography>
        </Stack>
      </Paper>

      {/* C13.7 (S272): the vision is on, the scope is the protected content and nothing is protected yet. */}
      {/* C14.3 (S285, S287): covered, the failure and the choice; after it, or uncovered by itself, the result (R-8). */}
      {failing && <FailureNotice summary={summary} onWait={() => cover.decide('wait')} onTurnOff={() => saveSwitch('false')} t_={t_} />}
      {cover.failureSeen && !failing && <ResultNotice turnedOff={cover.decision === 'off'} onBack={() => navigate(-1)} t_={t_} />}

      {enabled && scope === 'SENSITIVE' && nothingProtected(summary) && (
        <Alert severity='warning' variant='outlined'>{t_('Nothing is protected yet: choose screens or single items in What is protected')}</Alert>
      )}

      <Grid container spacing={3} alignItems='flex-start'>
        <Grid item xs={12} lg={8}>
          <Stack spacing={2.5}>
            <StepCard id='camera-control-scope' title={t_('Vigilance scope')}>
              <Grid container spacing={2}>
                {SCOPES.map((option) => (
                  <Grid item xs={12} md={6} key={option.value}>
                    <ButtonBase
                      aria-pressed={scope === option.value}
                      onClick={() => setValue(VISION_SCOPE, option.value)}
                      sx={cardSx(scope === option.value)}
                    >
                      <Stack direction='row' alignItems='center' spacing={1.25}>
                        {SCOPE_ICON[option.value]}
                        <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>{t_(option.text)}</Typography>
                      </Stack>
                      <Typography variant='body2' sx={{ color: '#CFCFCF' }}>{t_(option.line)}</Typography>
                    </ButtonBase>
                  </Grid>
                ))}
              </Grid>
            </StepCard>
            <SessionsBlock
              enabled={enabled}
              sessions={visionSessions.sessions}
              storedMax={storedMax}
              maxValue={values[VISION_MAX_WATCHED_SESSIONS] || ''}
              maxInvalid={invalid[VISION_MAX_WATCHED_SESSIONS] === true}
              onMaxChange={(value) => setValue(VISION_MAX_WATCHED_SESSIONS, value)}
              showProtected={String(values[VISION_UNWATCHED_SHOWS_PROTECTED] || '').toUpperCase() === 'YES'}
              onShowProtected={(checked) => setValue(VISION_UNWATCHED_SHOWS_PROTECTED, checked ? 'YES' : 'NO')}
              allMic={scopeIsAllMic}
              dimmedOpacity={DIMMED_OPACITY}
              onWatch={(id, instead) => visionSessions.watch(id, instead)}
              onStop={(id) => visionSessions.stopWatching(id)}
              t_={t_}
            />
            <StepCard
              id='camera-control-step-1'
              number={1}
              title={t_('What is protected')}
              help={[t_('The camera is on while MIC is open; only what is protected is hidden')]}
            >
              <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}>{t_('Whole screens')}</Typography>
              <Grid container spacing={1.5} data-testid='protected-screens' sx={{ opacity: scopeIsAllMic ? DIMMED_OPACITY : 1 }}>
                {sensitiveScreens.screens.map((row) => (
                  <Grid item xs={12} sm={6} lg={3} key={row.key}>
                    <Paper sx={{ ...cardSx(row.sensitive === true), flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>{t_(row.name)}</Typography>
                      <Switch
                        disabled={scopeIsAllMic}
                        checked={row.sensitive === true}
                        onChange={(event) => sensitiveScreens.setSensitive(row.key, event.target.checked)}
                        inputProps={{ role: 'switch', 'aria-label': t_(row.name) }}
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              {/* C15.3 (K2): what is protected one by one is chosen here, by name; no counter chips (H-C15-6). */}
              <SingleItems items={sensitiveItems} disabled={scopeIsAllMic} opacity={scopeIsAllMic ? DIMMED_OPACITY : 1} t_={t_} />
            </StepCard>

            <StepCard
              id='camera-control-step-2'
              number={2}
              title={t_('What is covered when a phone is detected')}
              help={COVER_MODES.map((mode) => t_(mode.help))}
            >
              <Grid container spacing={2} data-testid='cover-modes' sx={{ opacity: scopeIsAllMic ? DIMMED_OPACITY : 1 }}>
                {COVER_MODES.map((mode) => (
                  <Grid item xs={12} md={6} key={mode.value}>
                    <ButtonBase
                      aria-pressed={coverMode === mode.value}
                      disabled={scopeIsAllMic}
                      onClick={() => setValue(VISION_COVER_MODE, mode.value)}
                      sx={cardSx(coverMode === mode.value)}
                    >
                      <Stack direction='row' alignItems='center' spacing={1}>
                        <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>{t_(mode.text)}</Typography>
                        {mode.value === 'ALL' && <Chip size='small' label={t_('Safest')} sx={{ bgcolor: 'primary.main', color: '#133222', fontWeight: 700 }} />}
                      </Stack>
                    </ButtonBase>
                  </Grid>
                ))}
              </Grid>
            </StepCard>

            <StepCard id='camera-control-step-3' number={3} title={t_('When it acts')} help={RULES_HELP.map((text) => t_(text))}>
              <Grid container spacing={1.75}>
                {RULE_GROUPS.map((group) => (
                  <Grid item xs={12} md={4} key={group.title}>
                    <Stack spacing={1.5} sx={{ bgcolor: '#383838', borderRadius: 3, p: 2, height: '100%' }}>
                      <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>{t_(group.title)}</Typography>
                      {group.fields.map(numberField)}
                    </Stack>
                  </Grid>
                ))}
              </Grid>
              <Button
                aria-expanded={advanced}
                onClick={() => setAdvanced(!advanced)}
                startIcon={advanced ? <ExpandLessRounded /> : <ExpandMoreRounded />}
                sx={{ alignSelf: 'flex-start' }}
              >
                {t_('Advanced settings')}
              </Button>
              {advanced && (
                <Stack direction='row' flexWrap='wrap' gap={1.75} sx={{ borderTop: '1px solid #555555', pt: 1.75 }}>
                  {ADVANCED_FIELDS.map(numberField)}
                </Stack>
              )}
            </StepCard>

            <StepCard
              id='camera-control-step-4'
              number={4}
              title={t_('Detection model')}
              help={MODEL_HELP.map((text) => t_(text))}
              action={(
                <Stack direction='row' alignItems='center' spacing={1} data-testid='service-state' sx={{ color: '#CFCFCF', fontSize: 13 }}>
                  <Box component='span' data-testid='service-dot' sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: service.color, display: 'inline-block' }} />
                  <span>{service.text ? t_(service.text, { model: t_(service.model || modelText) }) : ''}</span>
                </Stack>
        )}
            >
              <Grid container spacing={2}>
                {MODELS.map((option) => (
                  <Grid item xs={12} md={6} key={option.value}>
                    <ButtonBase
                      aria-pressed={model === option.value}
                      onClick={() => setValue(VISION_MODEL, option.value)}
                      sx={cardSx(model === option.value)}
                    >
                      <Stack direction='row' alignItems='center' spacing={1}>
                        <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>{t_(option.text)}</Typography>
                        {option.recommended && <Chip size='small' label={t_('Recommended')} sx={{ bgcolor: 'primary.main', color: '#133222', fontWeight: 700 }} />}
                      </Stack>
                      <Stack direction='row' spacing={3.5}>
                        {[
                          [`${formatNumber(option.secondsPerImage, language)} s`, t_('per image')],
                          [formatPercent(option.phonesDetected, language), t_('phones detected')],
                          [option.users.map((count) => formatNumber(count, language)).join('–'), t_(option.usersText)]
                        ].map(([figure, label]) => (
                          <Stack key={label} data-testid='model-figure'>
                            <Typography component='span' sx={{ fontSize: 22, fontWeight: 700, color: 'text.primary' }}>{figure}</Typography>
                            {' '}
                            <Typography component='span' variant='caption' sx={{ color: '#A8A8A8' }}>{label}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </ButtonBase>
                  </Grid>
                ))}
              </Grid>
            </StepCard>

            <PhoneImagesBlock
              imageSaved={imageSaved}
              savedBlur={savedBlur}
              savedDays={savedDays}
              blur={String(values[VISION_PHONE_EVIDENCE_BLUR_FACES] || '').toUpperCase() !== 'NO'}
              days={values[VISION_PHONE_EVIDENCE_DAYS] || ''}
              daysInvalid={invalid[VISION_PHONE_EVIDENCE_DAYS] === true}
              onSaveImage={(on) => saveImage(on)}
              onBlur={(checked) => setValue(VISION_PHONE_EVIDENCE_BLUR_FACES, checked ? 'YES' : 'NO')}
              onDays={(value) => setValue(VISION_PHONE_EVIDENCE_DAYS, value)}
              available={evidence.available}
              onDeleteAll={() => deleteAllImages()}
              dimmedOpacity={DIMMED_OPACITY}
              t_={t_}
            />
          </Stack>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Paper component='aside' aria-label={t_('Summary')} sx={{ p: 2.5, borderRadius: 3, bgcolor: 'background.card', border: '1px solid #3A3A3A' }}>
            <Stack spacing={1.75}>
              <Typography variant='subtitle1' component='h2' sx={{ fontWeight: 600, color: 'text.primary' }}>{t_('Summary')}</Typography>
              <Stack component='dl' spacing={1.5} sx={{ m: 0 }}>
                {[
                  ['Status', enabled ? t_('Activated') : t_('Deactivated')],
                  ['What is protected', protectedText(sensitiveScreens.screens, t_, language)],
                  ['If a phone appears:', t_(coverModeText)],
                  ['Detection model', t_(modelText)],
                  // "None" exists as «Ninguno»; for the sessions it is its context "sessions" («Ninguna»).
                  ['Sessions', enabled ? t_('{{count}} of {{max}} watched', { count: watchedCount, max: storedMax }) : t_('None', { context: 'sessions' })]
                ].map(([term, value]) => (
                  <Stack key={term} data-summary-item spacing={0.25}>
                    <Typography component='dt' variant='caption' sx={{ color: '#A8A8A8' }}>{t_(term)}</Typography>
                    <Typography component='dd' variant='body2' sx={{ m: 0, color: 'text.primary', fontWeight: term === 'Status' ? 700 : 400 }}>{value}</Typography>
                  </Stack>
                ))}
              </Stack>
              {/* The incident log asks SETTINGS too, like this screen; it offers the way back here (Registro.dc.html). */}
              <Link component={RouterLink} to='/maintenance/visionIncidents' state={FROM_CAMERA_CONTROL} underline='none' sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                {t_('Vision incidents')}
                <ChevronRightRounded fontSize='small' />
              </Link>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Stack direction='row' justifyContent='flex-end' spacing={2}>
        <Button variant='outlined' onClick={() => navigate(-1)} sx={{ borderRadius: 999, minHeight: 48 }}>{t_('Back')}</Button>
      </Stack>
      <SensitiveMarkErrorSnackbar message={sensitiveScreens.saveError} onClose={() => sensitiveScreens.clearSaveError()} />
      {/* RN101 (S270): a fixed bar, only while something differs from what is stored. */}
      {dirty && (
        <Paper
          component='section'
          aria-label={t_('Unsaved changes')}
          elevation={8}
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2,
            px: 3,
            py: 1.5,
            bgcolor: 'background.card',
            borderTop: '1px solid #3A3A3A',
            borderRadius: 0
          }}
        >
          {/* Main.dc.html (S270): an amber dot before the text. */}
          <Box data-testid='unsaved-dot' sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#FF8F00', flex: '0 0 auto' }} />
          <Typography sx={{ mr: 'auto', fontWeight: 600, color: 'text.primary' }}>{t_('Unsaved changes')}</Typography>
          <Button variant='outlined' onClick={discardAll} sx={{ borderRadius: 999, minHeight: 48 }}>{t_('Discard')}</Button>
          <Button variant='contained' onClick={save} sx={{ borderRadius: 999, minHeight: 48 }}>{t_('Save changes')}</Button>
        </Paper>
      )}
      {confirming && (
        <ActivationDialog
          screens={sensitiveScreens.screens}
          coverMode={coverMode}
          model={model}
          scope={scope}
          imageSaved={storedImageSaved}
          savedBlur={savedBlur}
          savedDays={savedDays}
          onActivate={activate}
          onCancel={() => setConfirming(false)}
        />
      )}
    </Stack>
  )
}

const NO_FEEDBACK = { severity: 'success', messages: [] }

// Story 213, adenda C11.4 (DA-6), C12.4 and C14.1 — `BACKOFFICE › Camera control`, its own route. Only for the profiles
// SUPERADMIN and ADMIN (DR-4): for anybody else the screen renders nothing and asks Sun for nothing (the route guard sends
// the user away).
const CameraControl = () => {
  const { t_ } = useSmartTranslation()
  const { user } = useUser()
  // DB-6: whoever opens the screen edits the screen switches, also with the vision off.
  const canConfigure = canConfigureCameraControl(user)
  const [loadCount, setLoadCount] = useState(0)
  const [feedback, setFeedback] = useState(NO_FEEDBACK)
  // C14.3: whether this session reached the page covered, whether it saw a failure, and what was decided; kept here so a
  // remount after a save keeps it. Only a failure (RN114) is warned of and leaves a result notice.
  const { state } = useVision()
  const covered = state.covered === true
  const failure = covered && FAILURE_COVER_REASONS.includes(state.coverReason)
  const [arrivedCovered, setArrivedCovered] = useState(covered)
  const [failureSeen, setFailureSeen] = useState(failure)
  const [decision, setDecision] = useState('')

  useEffect(() => {
    if (covered) {
      setArrivedCovered(true)
    }
  }, [covered])

  useEffect(() => {
    if (failure) {
      setFailureSeen(true)
    }
  }, [failure])

  if (!canConfigure) {
    return null
  }

  const onSaved = (modelPending) => {
    setFeedback({ severity: 'success', messages: modelPending ? ['Saved', 'It will apply in a few seconds'] : ['Saved'] })
    setLoadCount((count) => count + 1)
  }

  const onError = (message) => setFeedback({ severity: 'error', messages: [message] })

  const onNotice = (message) => setFeedback({ severity: 'success', messages: [message] })

  return (
    <>
      <CameraControlScreen
        key={loadCount}
        onSaved={onSaved}
        onError={onError}
        onNotice={onNotice}
        backofficeLink={user.profile.code === 'SUPERADMIN'}
        cover={{ failure, failureSeen, arrivedCovered, decision, decide: setDecision }}
      />
      <Snackbar open={feedback.messages.length > 0} autoHideDuration={8000} onClose={() => setFeedback(NO_FEEDBACK)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert data-testid='camera-control-feedback' severity={feedback.severity} variant='filled' onClose={() => setFeedback(NO_FEEDBACK)}>
          {feedback.messages.map((message) => <div key={typeof message === 'object' ? message.key || message.text : message}>{settingsMessageText(message, t_)}</div>)}
        </Alert>
      </Snackbar>
    </>
  )
}

export default CameraControl
